/* eslint-disable @typescript-eslint/no-explicit-any */
import useUpdateAgent from '@/hooks/useUpdateAgent';
import { Edit3 } from 'lucide-react';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { formatColumnName } from './ImportMapping/ImportedData';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SelectForm from './ui/SelectForm';
import useGetMortgageCompanies from '@/hooks/useGetMortgageCompanies';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  agent: any;
  columns: string[];
}

const LoanOfficerFormModal: FC<Props> = ({
  agent,
  isOpen,
  setIsOpen,
  columns,
}) => {
  const { data: mortgageCompanies } = useGetMortgageCompanies();
  const { mutate: updateAgent, isPending: updatePending } = useUpdateAgent();
  const [editingRow, setEditingRow] = useState(agent);

  const getEditableFields = () => {
    return columns.filter(
      (col) => col !== 'inviteSent' && col !== 'registeredAt',
    );
  };

  const handleFieldChange = (field: string, value: string) => {
    if (editingRow) {
      setEditingRow((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleUpdateAgent = () => {
    updateAgent(
      {
        id: editingRow._id,
        ...editingRow,
      },
      {
        onSuccess: () => {
          toast.success('Successfully Edited Agent');
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Record
          </DialogTitle>
        </DialogHeader>

        {agent && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getEditableFields().map((field) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={field} className="text-sm font-medium">
                    {formatColumnName(field)}
                  </Label>
                  <Input
                    id={field}
                    value={editingRow[field] || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={`Enter ${formatColumnName(
                      field,
                    ).toLowerCase()}`}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label htmlFor="mortgage" className="text-sm font-medium">
                  {formatColumnName('mortgage Company')}
                </Label>
                <SelectForm
                  placeholder="Select Mortgage Company"
                  items={mortgageCompanies?.map((company) => ({
                    label: company.name,
                    value: company._id,
                  }))}
                  defaultValue={editingRow.mortgageId || ''}
                  onChange={(val) => handleFieldChange('mortgageId', val)}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            disabled={updatePending}
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateAgent} disabled={updatePending}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoanOfficerFormModal;
